import { getLanguageModules } from './modules/module-registry';
import { LanguageResources } from './language-resources';
import type { ILanguageResources } from './interfaces';
import { getV6FrameConfigLanguageModules } from './scriptsV6/v6-frameconfig-module-registry';
import { getSupplierLanguageModules } from './modules/supplier-module-registry';
import { getLicenseServerLanguageModules } from './modules/licenseserver-module-registry';

// eslint-disable-next-line @typescript-eslint/no-extraneous-class
export class DefaultLangBuilder {
  public static default(): ILanguageResources {
    const lr = new LanguageResources();
    lr.name = 'ui';
    const modules = getLanguageModules();
    for (const module of modules) {
      if (module.registerItems) {
        module.registerItems(lr);
      }
    }

    return lr;
  }

  public static supplierDefault(): ILanguageResources {
    const lr = new LanguageResources();
    lr.name = 'ui';
    const modules = getSupplierLanguageModules();
    for (const module of modules) {
      if (module.registerItems) {
        module.registerItems(lr);
      }
    }

    return lr;
  }

  public static licenseServerDefault(): ILanguageResources {
    const lr = new LanguageResources();
    lr.name = 'license-server-ui';
    const modules = getLicenseServerLanguageModules();
    for (const module of modules) {
      if (module.registerItems) {
        module.registerItems(lr);
      }
    }

    return lr;
  }

  public static v6FrameConfig(): ILanguageResources {
    const lr = new LanguageResources();
    lr.name = 'frameconfig';

    const modules = getV6FrameConfigLanguageModules();
    for (const module of modules) {
      if (module.registerItems) {
        module.registerItems(lr);
      }
    }

    return lr;
  }

  private static escapeXml(text: string): string {
    return text
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&apos;');
  }

  public static exportLanguageResourcesAsXliff(
    languageResources: ILanguageResources,
    targetLang: string = 'en'
  ): string {
    const xliff: string[] = [];
    xliff.push('<?xml version="1.0" encoding="UTF-8"?>');
    xliff.push(
      '<xliff xmlns="urn:oasis:names:tc:xliff:document:2.0" version="2.0" srcLang="en" trgLang="' + targetLang + '">'
    );
    xliff.push('<file id="resources" original="resources.xliff">');

    for (const item of Array.from(languageResources.getEnumerator())) {
      xliff.push(`<unit id="${this.escapeXml(item.id)}">`);

      if (item.description) {
        xliff.push('<notes><note>' + this.escapeXml(item.description) + '</note></notes>');
      }

      const segments = item.getSegments();
      for (let i = 0; i < segments.length; i++) {
        const segmentName = i === 0 ? 'default' : segments[i];
        const segmentContent = item.getSegment(segments[i]);

        xliff.push(`<segment${i > 0 ? ` id="${segmentName}"` : ''}>`);
        xliff.push('<source>' + this.escapeXml(segmentContent?.source ?? '') + '</source>');
        xliff.push('<target>' + this.escapeXml(segmentContent?.target ?? '') + '</target>');
        xliff.push('</segment>');
      }

      xliff.push('</unit>');
    }

    xliff.push('</file>');
    xliff.push('</xliff>');

    return xliff.join('\n');
  }

  public static readTargetLanguageFromXliff(xliffContent: string): ILanguageResources {
    const parser = new DOMParser();
    const doc = parser.parseFromString(xliffContent, 'application/xml');
    const languageResources = new LanguageResources();

    const xliff = doc.querySelector('xliff');
    if (!xliff || xliff.getAttribute('version') !== '2.0') {
      throw new Error('Invalid XLIFF version. Expected 2.0.');
    }

    const targetLang = xliff.getAttribute('trgLang');
    if (!targetLang) {
      throw new Error('Target language (trgLang) is missing in the XLIFF file.');
    }
    languageResources.language = targetLang;

    const units = doc.querySelectorAll('unit');
    units.forEach(unit => {
      const unitId = unit.getAttribute('id');
      if (!unitId) return;

      const notes = unit.querySelector('note');
      const description = notes ? notes.textContent || '' : '';

      const segments: Record<string, string> = {};
      unit.querySelectorAll('segment').forEach(segment => {
        const segmentId = segment.getAttribute('id') || 'default';
        const target = segment.querySelector('target');
        const source = segment.querySelector('source');
        segments[segmentId] = (target?.textContent || source?.textContent || '').trim();
      });

      const item = languageResources.add(unitId, segments['default'], description);
      for (const [key, value] of Object.entries(segments)) {
        if (key !== 'default') {
          item.addSegment(key, value);
        }
      }
    });

    return languageResources;
  }
}
